import { createApiRef } from '@backstage/frontend-plugin-api';
import type {
  DeploymentSnapshot,
  FleetAnchorsResponse,
  MfeDetailResponse,
  NegotiationSimulateRequest,
  OverviewResponse,
  ReadinessResponse,
  SimulationResponse,
} from '@internal/plugin-make-shell-common';

// Wire-contract DTOs live in the shared common package, which owns the Zod
// schemas the backend validates make-shell's responses against. Re-exported
// here so components import their types from one place. Type-only on purpose:
// the schemas themselves are runtime values, and the browser has no reason to
// carry Zod. The runtime exports are plain constants and the devbox URL rule,
// which the Devbox tab checks before the backend enforces it.
export {
  DEVBOX_PREVIEW_DOMAIN,
  SHELL_MFE_NAME,
  parseDevboxUrl,
} from '@internal/plugin-make-shell-common';

export type {
  BucketDiagnosticsEntry,
  BucketsDiagnosticsResponse,
  CdnArtefact,
  CommitDetails,
  FleetAnchor,
  FleetAnchorsResponse,
  FleetBucketDiagnostics,
  FleetBucketMember,
  FleetCommitDetails,
  FleetCommitDiagnostics,
  FleetDiagnostics,
  FleetMemberDiagnostics,
  FleetsDiagnosticsResponse,
  FleetsSnapshot,
  CdnArtefactStatus,
  CdnUrlProvenance,
  CspBucketDiagnosticsEntry,
  CspConditionalRule,
  CspDiagnosticsDump,
  CspDirectives,
  CspExternalSource,
  DeploymentDiagnosticsResponse,
  DeploymentRemoteProjection,
  DeploymentSnapshot,
  DevboxUrl,
  LibraryCell,
  LibraryStatus,
  ManifestsResponse,
  MfeCspConfig,
  MfeDetailResponse,
  MfeReadiness,
  MfManifest,
  MfManifestExpose,
  MfManifestShared,
  MfVersionEntry,
  MfVersionFile,
  MfVersionLatest,
  MfVersionsIndex,
  NegotiationConflict,
  NegotiationDiagnostics,
  NegotiationSimulateRequest,
  NegotiationSimulateResponse,
  NegotiationStrategy,
  OverviewResponse,
  ReadinessResponse,
  RemoteManifestEntry,
  RequestedVersions,
  ResolvedVersions,
  ShellEndpoint,
  ShellEnvironment,
  ShellMode,
  SimulationErrorKind,
  SimulationResponse,
  SnapshotErrors,
  SnapshotStatus,
  SharedContractsConflict,
  SharedContractsMismatch,
  SingletonVerificationSummary,
  UnverifiedSharedDep,
  WiderThanPolicyDep,
  VersionInfo,
  VersionResponse,
} from '@internal/plugin-make-shell-common';

export interface MakeShellApi {
  /** The aggregate for every configured deployment, in one round trip. */
  getOverview(): Promise<OverviewResponse>;

  /** Drops the backend's TTL cache and resolves with the fresh aggregate. */
  refresh(): Promise<OverviewResponse>;

  /**
   * One MFE on one deployment, with the CDN files behind it. Fetched on demand
   * rather than as part of the aggregate: reading five files per MFE across the
   * fleet would be hundreds of CDN requests for a page that shows none of them.
   */
  getMfeDetail(
    deploymentId: string,
    mfeName: string,
  ): Promise<MfeDetailResponse>;

  /**
   * Dry-run negotiation against one deployment (UP-2551). Deployment-level
   * failures ride inside the response envelope rather than as HTTP errors.
   */
  simulate(
    deploymentId: string,
    request: NegotiationSimulateRequest,
  ): Promise<SimulationResponse>;

  /**
   * MFE integration readiness checklist (UP-2552). Declared
   * `@integromat/make-shell-*` versions vs the baseline on make-shell master.
   */
  getReadiness(): Promise<ReadinessResponse>;

  /**
   * Every deployment's fleet ledger, with GitHub's record of the commits it
   * names. Read when the Fleet anchors view opens, never by the poll.
   */
  getFleetAnchors(): Promise<FleetAnchorsResponse>;

  /**
   * One devbox preview, polled on demand into the same snapshot a configured
   * deployment contributes to the overview. `url` must pass `parseDevboxUrl`;
   * the backend refuses anything else with a 400.
   */
  getDevboxSnapshot(url: string): Promise<DeploymentSnapshot>;
}

export const makeShellApiRef = createApiRef<MakeShellApi>().with({
  id: 'plugin.make-shell.api',
});
